import { useEffect, useState } from 'react';
import { ImageIcon, RefreshCw, ArrowLeft, X, Undo2, Check, Loader2, LayoutTemplate } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PreparedImage, EntityImageRow } from '../../lib/api/articles.js';
import {
  applyArticleImageEdits,
  reselectArticleImages,
  readImageRevision,
  listImageRevisions,
  replaceArticleImage,
  suppressArticleImageFallback,
  regenerateHeadlineCard,
  suppressHeadlineCard,
} from '../../lib/api/articles.js';
import { EntityImagePickerDialog } from './EntityImagePickerDialog.js';
import { ImageLightbox } from '../images/ImageLightbox.js';
import { handleMutationConflict } from '../../lib/mutationConflict.js';
import { announcementImages, headlineCardState } from '../../lib/headlineCard.js';

interface ArticleImagesPanelProps {
  articleId: string;
  /** True when this article is live on a `main_branch` destination.
   *  Drives the "committing publishes to readers" warning — the behaviour
   *  itself has always been there, it was simply never stated. */
  isLiveOnProduction?: boolean;
  /** The article's `updated_at` ISO string — used as the optimistic-lock
   *  token on Apply/Replace. Pass null while the article is loading. */
  expectedUpdatedAt: string | null;
  preparedImages: PreparedImage[] | null | undefined;
  /** Count of raw announcement images in article_metadata.images — when
   *  the curated set is empty but raw images exist, the publishing
   *  fallback may still render them live (invisible in this panel). */
  rawImageCount?: number;
  /** articles.image_history_head_id — non-null once any operator image
   *  edit exists, which also flips the renderer to curated-only. */
  imageHistoryHeadId?: string | null;
  entityDisplayName?: string | null;
  /** `article_metadata.headline_card_suppressed` — the operator dropped the
   *  generated headline card for this article (static default card ships). */
  headlineCardSuppressed?: boolean;
  /** Disable interactions while the article isn't editable
   *  (e.g. archived, sending notification). */
  disabled?: boolean;
}

export function ArticleImagesPanel({
  articleId,
  isLiveOnProduction = false,
  expectedUpdatedAt,
  preparedImages,
  rawImageCount = 0,
  imageHistoryHeadId = null,
  entityDisplayName,
  headlineCardSuppressed = false,
  disabled,
}: ArticleImagesPanelProps) {
  const queryClient = useQueryClient();
  const [pickerImage, setPickerImage] = useState<PreparedImage | null>(null);
  // Preview index into the article's own images — the operator compares what
  // is in the slot against the candidates before replacing it.
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);

  // Staged edits: Remove only MARKS an image, caption typing only DRAFTS
  // a change. Nothing hits the backend until Apply, so a multi-image
  // cleanup lands as ONE edit (one history row / single undo, one
  // re-render, one republish per target) instead of a burst of racing
  // publish + auto-promote jobs (the 2026-07-22 CAE stale-promote
  // incident).
  const [staged, setStaged] = useState<Set<string>>(new Set());
  // image_id → draft caption text, present only while it differs from
  // the stored caption.
  const [captionDrafts, setCaptionDrafts] = useState<Map<string, string>>(new Map());

  /**
   * Staged swaps: outgoing image_id -> the incoming pool row.
   *
   * Replace was the ONE image operation that never got the batch treatment
   * (D9). It fired immediately with queueRepublish, so two replaces produced
   * two re-renders and two republishes — the fan-out shape of the 2026-07-22
   * CAE incident, in the operation that never received that incident's own
   * fix. Staging it also lifts the restriction that used to disable Replace
   * whenever anything else was staged, because the two can no longer race.
   */
  const [stagedReplacements, setStagedReplacements] =
    useState<Map<string, EntityImageRow>>(new Map());

  /**
   * A staged WHOLE-SET proposal — a re-selection or a restored revision.
   *
   * These differ in kind from per-image edits: they propose an entire new
   * image set rather than adjusting the current one, so they supersede any
   * pending removals/captions/replacements rather than combining with them.
   * Mixing the two would make the resulting history row mean two things and
   * undo from it ambiguous.
   *
   * Both were previously IMMEDIATE, and both lived in the unpublish dropdown
   * rather than beside the images they change (§5.2c).
   */
  const [stagedSet, setStagedSet] = useState<
    | { kind: 'reselect'; images: PreparedImage[]; note: string }
    | { kind: 'restore'; images: PreparedImage[]; historyId: string; note: string }
    // Suppression has no images to preview by definition — its whole effect is
    // "this article is deliberately imageless". It stages anyway so that every
    // one of the seven edit operations goes through the same commit, rather
    // than six doing so and one writing on a click.
    | { kind: 'suppress'; images: PreparedImage[]; note: string }
    | null
  >(null);
  const [setError, setSetError] = useState<string | null>(null);

  // Drop stale staging if the panel is remounted onto another article.
  /**
   * The article's `updated_at` as it was when staging began.
   *
   * ⚠ WHY THIS EXISTS. `expectedUpdatedAt` is a LIVE prop: when another
   * operator edits this article, the WS handler invalidates ['article', id],
   * the article refetches, and the prop silently becomes the NEW token. Sending
   * that token would make the optimistic-lock check pass — so a staged batch
   * would apply cleanly on top of a set the operator never saw. The lock exists
   * precisely to stop that, and the live prop defeated it.
   *
   * Staging made the exposure far worse: the window used to be the
   * milliseconds between click and write; it is now however long someone
   * leaves edits pending.
   *
   * Pinning means a concurrent change fails the commit LOUDLY (409) instead of
   * applying silently, and `staleBaseline` below turns that into a warning
   * before the operator even tries.
   */
  const [stagedBaseline, setStagedBaseline] = useState<string | null>(null);
  /**
   * Operator opt-out: land this commit on preview and go no further.
   *
   * A completed publish job triggers auto-promote, and that hook fires for ANY
   * successful publish — including an edit's republish. Measured 2026-08-26:
   * 59 of 61 republishes in seven days queued a promote. So a commit on a live
   * article has always reached readers; it was simply never said anywhere.
   * Default off, because the common case is a correction that SHOULD ship.
   */
  const [previewOnly, setPreviewOnly] = useState(false);

  /** The token a write must carry: pinned once staging starts. */
  const lockToken = () => stagedBaseline ?? expectedUpdatedAt;

  /** Begin (or continue) a staging session, pinning the token on first edit. */
  const beginStaging = () => {
    setStagedBaseline((prev) => prev ?? expectedUpdatedAt);
  };


  useEffect(() => {
    setStaged(new Set());
    setCaptionDrafts(new Map());
    setStagedReplacements(new Map());
    setStagedSet(null);
    setStagedBaseline(null);
    setSetError(null);
    setPreviewOnly(false);
  }, [articleId]);

  const applyMutation = useMutation({
    mutationFn: (edits: {
      removals: string[];
      captions: Array<{ image_id: string; caption: string | null }>;
      replacements: Array<{ image_id: string; new_image_id: string }>;
      preview_only: boolean;
    }) => applyArticleImageEdits(articleId, edits, lockToken(), true),
    onSuccess: () => {
      setStaged(new Set());
      setCaptionDrafts(new Map());
      setStagedReplacements(new Map());
      setStagedBaseline(null);
      queryClient.invalidateQueries({ queryKey: ['article', articleId] });
      queryClient.invalidateQueries({ queryKey: ['article-image-history', articleId] });
      queryClient.invalidateQueries({ queryKey: ['article-publishing-history', articleId] });
    },
    onError: (err: unknown) => handleMutationConflict(err, queryClient, [['article', articleId]]),
  });

  /** Ask the engine what it WOULD pick. Writes nothing. */
  const previewReselect = useMutation({
    mutationFn: () =>
      reselectArticleImages(articleId, lockToken(), false, { dryRun: true }),
    onSuccess: (res) => {
      setSetError(null);
      if (!res.changed) {
        setSetError('Re-selection would pick exactly the current images — nothing to apply.');
        return;
      }
      beginStaging();
      setStagedSet({
        kind: 'reselect',
        images: res.proposed_images ?? [],
        note: `${res.after.inline_count} inline${
          res.after.featured_image_id ? ' + hero' : ', no hero'
        }`,
      });
    },
    onError: (err: any) =>
      setSetError(err?.response?.data?.detail || err?.message || 'Re-selection preview failed'),
  });

  /** Read a stored revision without moving the head pointer. */
  const previewRevision = useMutation({
    mutationFn: (historyId: string) => readImageRevision(articleId, historyId),
    onSuccess: (res) => {
      setSetError(null);
      beginStaging();
      setStagedSet({
        kind: 'restore',
        images: res.images ?? [],
        historyId: res.history_id,
        note: `${res.change_kind.replace('_', ' ')} · ${res.images?.length ?? 0} image${
          (res.images?.length ?? 0) === 1 ? '' : 's'
        }`,
      });
    },
    onError: (err: any) =>
      setSetError(err?.response?.data?.detail || err?.message || 'Could not read that revision'),
  });

  /** Commit whichever whole-set proposal is staged. */
  const commitSet = useMutation({
    mutationFn: async () => {
      if (!stagedSet) return;
      if (stagedSet.kind === 'suppress') {
        return suppressArticleImageFallback(articleId, lockToken());
      }
      if (stagedSet.kind === 'restore') {
        return applyArticleImageEdits(
          articleId,
          {
            removals: [], captions: [], replacements: [],
            from_history_id: stagedSet.historyId,
            preview_only: previewOnly,
          },
          lockToken(), true,
        );
      }
      // Re-select commits by RE-RUNNING the engine (operator ruling): the
      // selection rules stay authoritative and no client-supplied image set
      // crosses the boundary. We echo what we previewed so the server can
      // tell us if it picked differently — its inputs move.
      return reselectArticleImages(articleId, lockToken(), true, {
        previewedImageIds: stagedSet.images.map((i) => i.image_id!).filter(Boolean),
        previewOnly,
      });
    },
    onSuccess: (res: any) => {
      setStagedSet(null);
      setStagedBaseline(null);
      setSetError(
        res?.diverged_from_preview
          ? 'Committed — but the engine picked differently than previewed'
            + (res?.divergence?.featured_changed ? ', including the hero.' : '.')
          : null,
      );
      queryClient.invalidateQueries({ queryKey: ['article', articleId] });
      queryClient.invalidateQueries({ queryKey: ['article-image-history', articleId] });
      queryClient.invalidateQueries({ queryKey: ['article-image-revisions', articleId] });
      queryClient.invalidateQueries({ queryKey: ['article-publishing-history', articleId] });
    },
    onError: (err: unknown) => handleMutationConflict(err, queryClient, [['article', articleId]]),
  });

  /**
   * Headline-card actions (MASTHEAD_HEADLINE_CARDS brief D8). Both write
   * IMMEDIATELY — the routes re-render/drop the card and queue the test
   * republish themselves, and there is nothing to preview: the card is a
   * deterministic render of fields the operator cannot edit here. They send
   * the LIVE token rather than the pinned staging baseline, and are disabled
   * while anything is staged, because a card write bumps `updated_at` and
   * would otherwise trip `staleBaseline` on the operator's own edits.
   */
  const onHeadlineCardError = (err: any) => {
    if (handleMutationConflict(err, queryClient, [['article', articleId]])) return;
    setSetError(err?.response?.data?.detail || err?.message || 'Headline card update failed');
  };
  const onHeadlineCardSuccess = () => {
    setSetError(null);
    queryClient.invalidateQueries({ queryKey: ['article', articleId] });
    queryClient.invalidateQueries({ queryKey: ['article-content-history', articleId] });
    queryClient.invalidateQueries({ queryKey: ['article-publishing-history', articleId] });
  };
  const regenerateCard = useMutation({
    mutationFn: () => regenerateHeadlineCard(articleId, expectedUpdatedAt, true),
    onSuccess: onHeadlineCardSuccess,
    onError: onHeadlineCardError,
  });
  const suppressCard = useMutation({
    mutationFn: (suppressed: boolean) =>
      suppressHeadlineCard(articleId, suppressed, expectedUpdatedAt, true),
    onSuccess: onHeadlineCardSuccess,
    onError: onHeadlineCardError,
  });

  /** The revision timeline — stored since mig 185, never surfaced until now. */
  const { data: revisionData } = useQuery({
    queryKey: ['article-image-revisions', articleId],
    queryFn: () => listImageRevisions(articleId),
    enabled: !!articleId,
  });

  const stageReplacement = (outgoingId: string, incoming: EntityImageRow) => {
    beginStaging();
    setStagedReplacements((prev) => {
      const next = new Map(prev);
      // Re-picking the same slot replaces the pending choice rather than
      // stacking a second swap on an image that no longer exists.
      next.set(outgoingId, incoming);
      return next;
    });
    setPickerImage(null);
  };

  const unstageReplacement = (outgoingId: string) => {
    setStagedReplacements((prev) => {
      const next = new Map(prev);
      next.delete(outgoingId);
      return next;
    });
  };

  const toggleStaged = (imageId: string) => {
    beginStaging();
    setStaged((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  };

  const setCaptionDraft = (imageId: string, value: string, original: string) => {
    beginStaging();
    setCaptionDrafts((prev) => {
      const next = new Map(prev);
      if (value === original) next.delete(imageId);
      else next.set(imageId, value);
      return next;
    });
  };

  const images = preparedImages || [];
  const featured = images.find((i) => i.role === 'featured') || null;
  const inline = images.filter((i) => i.role === 'inline');
  // The generated card is not a figure: it sits outside the count and the
  // per-image edit machinery (no image_id, nothing in Cynapse to swap).
  const cardState = headlineCardState(images, { headline_card_suppressed: headlineCardSuppressed });
  const totalCount = (featured ? 1 : 0) + inline.length;
  const busy = applyMutation.isPending;
  // Caption drafts on images marked for removal don't count — the
  // removal wins and the backend rejects contradictory pairs anyway.
  const effectiveCaptionEdits = [...captionDrafts.entries()].filter(
    ([imageId]) => !staged.has(imageId) && !stagedReplacements.has(imageId),
  );
  const changeCount =
    staged.size + effectiveCaptionEdits.length + stagedReplacements.size;
  const hasChanges = changeCount > 0;
  // A whole-set proposal supersedes per-image edits — see stagedSet's note.
  const perImageDisabled = stagedSet !== null;
  // Someone else changed this article while these edits sat staged. The commit
  // WILL be refused (the pinned token no longer matches), so say so now rather
  // than letting the operator find out by pressing the button.
  const staleBaseline =
    stagedBaseline !== null
    && expectedUpdatedAt !== null
    && expectedUpdatedAt !== stagedBaseline;
  const revisions = revisionData?.revisions ?? [];
  const headIdx = revisions.findIndex((r) => r.is_head);
  // The timeline is newest-first, so the PREVIOUS revision sits after head.
  const prevRevision = headIdx >= 0 ? revisions[headIdx + 1] : undefined;
  const nextRevision = headIdx > 0 ? revisions[headIdx - 1] : undefined;


  // Stated before the button, not discovered afterwards. This behaviour has
  // always existed; establishing it took a code trace plus a database
  // correlation, which is precisely why it needed saying here.
  const liveNotice = isLiveOnProduction ? (
    <label className="mt-2 flex items-start gap-2 text-xs cursor-pointer">
      <input
        type="checkbox"
        checked={previewOnly}
        onChange={(e) => setPreviewOnly(e.target.checked)}
        className="mt-0.5 flex-shrink-0"
      />
      <span className="text-grey-700">
        <strong className="text-grey-900">Preview only</strong> — hold this on
        preview instead of publishing it.
        <span className="block text-grey-500">
          This article is live. Committing publishes the change to readers.
        </span>
      </span>
    </label>
  ) : null;

  const applyChanges = () => {
    applyMutation.mutate({
      removals: [...staged],
      captions: effectiveCaptionEdits.map(([image_id, caption]) => ({
        image_id,
        caption: caption.trim() === '' ? null : caption.trim(),
      })),
      replacements: Array.from(stagedReplacements.entries()).map(([image_id, incoming]) => ({
        image_id,
        new_image_id: incoming.image_id,
      })),
      preview_only: previewOnly,
    });
  };

  const discardChanges = () => {
    setStaged(new Set());
    setCaptionDrafts(new Map());
    setStagedReplacements(new Map());
    setStagedBaseline(null);
  };


  // Fallback exposure: the curated set is empty, no operator edit has
  // ever pinned it, and raw announcement images exist — the publishing
  // service's legacy fallback may render those live even though this
  // panel truthfully shows zero (AGC 2026-07-23: a preparer-rejected
  // banner shipped with a PII contact-block caption). "Empty" excludes the
  // generated headline card, exactly as the publishing transformer's
  // emptiness test does: a card-only array is still an imageless article.
  const fallbackExposed =
    !!preparedImages
    && announcementImages(preparedImages).length === 0
    && !imageHistoryHeadId
    && rawImageCount > 0;

  if (!preparedImages) {
    // Article hasn't loaded yet — don't render an empty section.
    return null;
  }

  return (
    <div className="bg-surface rounded-xl border border-grey-200 p-8 shadow-md">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-grey-900 flex items-center gap-2">
          <ImageIcon className="h-5 w-5 text-grey-500" />
          Images ({totalCount})
        </h2>
        <p className="text-xs text-grey-500">
          Mark removals, edit captions and pick replacements, then Apply —
          all of it goes in one change, with one undo. Nothing reaches the
          backend until you apply. Republishes to test only.
        </p>
      </div>

      {totalCount === 0 && !fallbackExposed && (
        <div className="text-center py-8 text-grey-500">
          <p>No {cardState?.kind === 'card' ? 'announcement ' : ''}images on this article.</p>
        </div>
      )}

      {fallbackExposed && (
        <div className="rounded-lg border border-warning-300 bg-warning-50 px-4 py-4">
          <p className="text-sm text-warning-900 font-medium mb-1">
            No curated images — but the live site may still show one.
          </p>
          <p className="text-sm text-warning-800 mb-3">
            The image picker selected nothing for this article, but{' '}
            {rawImageCount} raw announcement image{rawImageCount === 1 ? '' : 's'}{' '}
            exist and the publisher's legacy fallback can render{' '}
            {rawImageCount === 1 ? 'it' : 'them'} (with{' '}
            {rawImageCount === 1 ? 'its caption' : 'their captions'}) on the
            live page. Suppressing pins this article as deliberately
            imageless — this masthead only; sibling articles are unaffected.
          </p>
          <button
            onClick={() => { beginStaging(); setStagedSet({
              kind: 'suppress',
              images: [],
              note: 'article pinned as deliberately imageless',
            }); }}
            disabled={disabled || busy || stagedSet !== null}
            className="px-3 py-1.5 text-xs font-semibold text-white bg-warning-600 rounded hover:bg-warning-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <X className="h-3.5 w-3.5" />
            Suppress images on this article
          </button>
        </div>
      )}

      {/*
        Re-select / undo / redo live HERE, beside the images they change.
        They were rendered inside the dropdown gated by `showUnpublishPicker`
        — the unpublish target picker — so an operator edited images in one
        place and rolled them back from a menu named for something else
        (§5.2c). All three now STAGE rather than apply.
      */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => previewReselect.mutate()}
          disabled={disabled || busy || previewReselect.isPending || stagedSet !== null}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-grey-700 border border-grey-300 rounded hover:border-grey-400 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Run the selection engine and show what it would pick. Writes nothing."
        >
          {previewReselect.isPending
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <RefreshCw className="h-3.5 w-3.5" />}
          Preview re-selection
        </button>
        {prevRevision && (
          <button
            type="button"
            onClick={() => previewRevision.mutate(prevRevision.history_id)}
            disabled={disabled || busy || previewRevision.isPending || stagedSet !== null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-grey-700 border border-grey-300 rounded hover:border-grey-400 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Show the previous image selection. Nothing is written until you apply."
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Preview previous
          </button>
        )}
        {nextRevision && (
          <button
            type="button"
            onClick={() => previewRevision.mutate(nextRevision.history_id)}
            disabled={disabled || busy || previewRevision.isPending || stagedSet !== null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-grey-700 border border-grey-300 rounded hover:border-grey-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className="h-3.5 w-3.5 rotate-180" />
            Preview next
          </button>
        )}
        {revisions.length > 0 && (
          <span className="text-xs text-grey-500">
            {revisions.length} revision{revisions.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {setError && (
        <div className="mb-4 rounded-lg border border-warning-300 bg-warning-50 px-4 py-2.5 text-sm text-warning-900">
          {setError}
        </div>
      )}

      {staleBaseline && (
        <div className="mb-4 rounded-lg border border-danger-300 bg-danger-50 px-4 py-3">
          <p className="text-sm font-semibold text-danger-900">
            This article changed while your edits were staged.
          </p>
          <p className="text-xs text-danger-800 mt-0.5">
            Someone else edited it, so the images above may no longer be the ones
            you marked up. Applying now would be refused. Discard and start
            again from the current set.
          </p>
          <button
            type="button"
            onClick={() => { discardChanges(); setStagedSet(null); setSetError(null); }}
            className="mt-2 px-3 py-1.5 text-xs font-semibold text-white bg-danger-600 rounded hover:bg-danger-700"
          >
            Discard my staged edits
          </button>
        </div>
      )}

      {stagedSet && (
        <div className="mb-4 rounded-lg border border-primary-300 bg-primary-50 px-4 py-3">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-primary-900">
                {stagedSet.kind === 'reselect'
                  ? 'Re-selection staged'
                  : stagedSet.kind === 'suppress'
                    ? 'Suppression staged'
                    : 'Previous selection staged'}
                <span className="font-normal text-primary-800"> — {stagedSet.note}</span>
              </p>
              <p className="text-xs text-primary-800 mt-0.5">
                Nothing is written until you apply. Applying replaces the whole
                set, so pending removals and captions are set aside.
              </p>
              {liveNotice}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => { setStagedSet(null); setSetError(null); }}
                disabled={commitSet.isPending}
                className="px-3 py-1.5 text-xs font-medium text-grey-700 border border-grey-300 rounded hover:border-grey-400 disabled:opacity-50"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={() => commitSet.mutate()}
                disabled={disabled || commitSet.isPending || staleBaseline}
                className="px-3 py-1.5 text-xs font-semibold text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                {commitSet.isPending
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Check className="h-3.5 w-3.5" />}
                Apply this selection
              </button>
            </div>
          </div>

          {/* The proposed set itself — image-level preview (D10). A stored
              revision or a dry-run can carry the headline card; it is not a
              figure, so it must not turn an imageless set into "Fig 1". */}
          <div className="mt-3 flex flex-wrap gap-2">
            {announcementImages(stagedSet.images).length === 0 ? (
              // Staging every image away must preview AS imageless, not as
              // "no preview" — that ambiguity is the CAE `prepared_images = []`
              // trap, and the case where being wrong costs most.
              <span className="text-xs font-medium text-primary-900">
                This selection is imageless — the article would render with no images.
              </span>
            ) : (
              announcementImages(stagedSet.images).map((img, i) => {
                const t = img.thumbnail_url || img.inline_url || img.url || img.hero_url;
                const isHero = img.role === 'featured';
                return (
                  <div key={img.image_id || i} className="w-24">
                    {t ? (
                      <img src={t} alt={img.alt || ''} className="w-24 h-16 object-cover rounded border border-primary-200" />
                    ) : (
                      <div className="w-24 h-16 rounded border border-primary-200 bg-grey-100 flex items-center justify-center">
                        <ImageIcon className="h-4 w-4 text-grey-400" />
                      </div>
                    )}
                    <p className="mt-0.5 text-[10px] text-primary-900 font-medium truncate">
                      {/* Hero claims Figure 1; inline figures follow it. */}
                      {isHero ? 'Hero · Fig 1' : `Fig ${i + 1}`}
                    </p>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {featured && (
          <ImageRow
            image={featured}
            label="Featured"
            labelClass="bg-primary-100 text-primary-800"
            stagedForRemoval={!!featured.image_id && staged.has(featured.image_id)}
            captionDraft={featured.image_id ? captionDrafts.get(featured.image_id) : undefined}
            onCaptionChange={(v) =>
              setCaptionDraft(featured.image_id!, v, featured.caption || '')
            }
            onToggleRemove={() => toggleStaged(featured.image_id!)}
            onZoom={() => setZoomIndex(images.indexOf(featured))}
            onReplace={() => setPickerImage(featured)}
            replaceDisabled={false}
            stagedReplacement={featured.image_id ? stagedReplacements.get(featured.image_id) : undefined}
            onUnstageReplacement={() => unstageReplacement(featured.image_id!)}
            disabled={disabled || busy}
          />
        )}
        {inline.map((img, idx) => (
          <ImageRow
            key={img.image_id || idx}
            image={img}
            label={`Inline #${idx + 1}`}
            labelClass="bg-grey-100 text-grey-800"
            stagedForRemoval={!!img.image_id && staged.has(img.image_id)}
            captionDraft={img.image_id ? captionDrafts.get(img.image_id) : undefined}
            onCaptionChange={(v) => setCaptionDraft(img.image_id!, v, img.caption || '')}
            onToggleRemove={() => toggleStaged(img.image_id!)}
            onZoom={() => setZoomIndex(images.indexOf(img))}
            onReplace={() => setPickerImage(img)}
            replaceDisabled={false}
            stagedReplacement={img.image_id ? stagedReplacements.get(img.image_id) : undefined}
            onUnstageReplacement={() => unstageReplacement(img.image_id!)}
            disabled={disabled || busy}
          />
        ))}
      </div>

      {cardState && (
        <HeadlineCardTile
          state={cardState}
          isLiveOnProduction={isLiveOnProduction}
          onZoom={cardState.kind === 'card'
            ? () => setZoomIndex(images.indexOf(cardState.entry))
            : undefined}
          onRegenerate={() => regenerateCard.mutate()}
          onSuppress={(suppressed) => suppressCard.mutate(suppressed)}
          regenerating={regenerateCard.isPending}
          suppressing={suppressCard.isPending}
          // Staged edits pin the lock token; a card write would stale them.
          blockedByStaging={hasChanges || stagedSet !== null}
          disabled={disabled || busy}
        />
      )}

      {hasChanges && (
        <div className="mt-5 flex items-center justify-between rounded-lg border border-warning-300 bg-warning-50 px-4 py-3">
          <p className="text-sm text-warning-900">
            {[
              staged.size > 0 && `${staged.size} removal${staged.size === 1 ? '' : 's'}`,
              stagedReplacements.size > 0
                && `${stagedReplacements.size} replacement${stagedReplacements.size === 1 ? '' : 's'}`,
              effectiveCaptionEdits.length > 0
                && `${effectiveCaptionEdits.length} caption edit${effectiveCaptionEdits.length === 1 ? '' : 's'}`,
            ].filter(Boolean).join(' · ')}
            <span> staged. Nothing changes until you apply.</span>
            {liveNotice}
            {staged.size > 0 && (
              // The featured caption claims Figure 1 and inline figures follow,
              // so removing any image renumbers everything below it. Say so
              // before the commit, not after someone spots it on the page.
              <span className="block mt-0.5 text-warning-800">
                Figures renumber on apply.
              </span>
            )}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={discardChanges}
              disabled={busy}
              className="px-3 py-1.5 text-xs font-medium text-grey-700 border border-grey-300 rounded hover:border-grey-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Discard changes
            </button>
            <button
              onClick={applyChanges}
              disabled={disabled || busy || staleBaseline}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {applyMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              Apply {changeCount} change{changeCount === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      )}

      {pickerImage && (
        <EntityImagePickerDialog
          articleId={articleId}
          swappingImageId={pickerImage.image_id || ''}
          swappingThumbUrl={pickerImage.thumbnail_url || pickerImage.inline_url || null}
          swappingRoleLabel={pickerImage.role}
          entityDisplayName={entityDisplayName}
          isOpen
          onClose={() => setPickerImage(null)}
          onApply={(newImageId, incoming) => {
            // STAGE it — nothing reaches the backend until Commit.
            stageReplacement(pickerImage.image_id!, incoming);
          }}
          isApplying={false}
        />
      )}

      {zoomIndex !== null && images[zoomIndex] && (
        <ImageLightbox
          items={images.map((img) => ({
            // Preview the largest we hold, not the thumbnail the row shows.
            url: img.url || img.hero_url || img.inline_url || img.thumbnail_url || '',
            alt: img.alt ?? null,
            title: img.caption || img.alt || img.image_type || null,
            subtitle: [
              img.role === 'featured' ? 'featured'
                : img.role === 'headline_card' ? 'generated card'
                  : 'inline',
              img.image_role,
              img.image_type,
              typeof img.quality_score === 'number' ? `q${img.quality_score.toFixed(2)}` : null,
            ].filter(Boolean).join(' · '),
          }))}
          index={zoomIndex}
          onIndex={setZoomIndex}
          onClose={() => setZoomIndex(null)}
        />
      )}
    </div>
  );
}

function ImageRow({
  image,
  label,
  labelClass,
  stagedForRemoval,
  captionDraft,
  onCaptionChange,
  onToggleRemove,
  onZoom,
  onReplace,
  replaceDisabled,
  stagedReplacement,
  onUnstageReplacement,
  disabled,
}: {
  image: PreparedImage;
  label: string;
  labelClass: string;
  stagedForRemoval: boolean;
  /** Set when a swap is staged for this slot — the row shows the INCOMING
   *  image, so the operator sees the result before committing rather than
   *  after it has already shipped to test. */
  stagedReplacement?: EntityImageRow;
  onUnstageReplacement?: () => void;
  /** Present only while the draft differs from the stored caption. */
  captionDraft: string | undefined;
  onCaptionChange: (value: string) => void;
  onToggleRemove: () => void;
  onZoom: () => void;
  onReplace: () => void;
  replaceDisabled?: boolean;
  disabled?: boolean;
}) {
  // Preview the INCOMING image when a swap is staged — the whole point of
  // staging is seeing the outcome before it is written.
  const thumb = stagedReplacement
    ? (stagedReplacement.thumbnail_url || stagedReplacement.inline_url
       || stagedReplacement.cdn_url || '')
    : (image.thumbnail_url || image.inline_url || image.url || image.hero_url || '');
  const originalCaption = image.caption || '';
  const captionValue = captionDraft !== undefined ? captionDraft : originalCaption;
  const captionEdited = captionDraft !== undefined && !stagedForRemoval;
  const provSource = image.provenance?.source || 'current';
  // Shown in the row rather than only in the summary bar: an operator
  // scanning the list should be able to see WHICH slot is changing.
  const swapNote = stagedReplacement ? (
    <div className="mt-1.5 flex items-center gap-2 text-xs">
      <span className="px-1.5 py-0.5 rounded bg-primary-100 text-primary-800 font-semibold">
        Replacement staged
      </span>
      <span className="text-grey-600">
        keeps this slot &amp; figure number
      </span>
      {onUnstageReplacement && (
        <button
          type="button"
          onClick={onUnstageReplacement}
          className="text-grey-500 underline hover:text-grey-700"
        >
          undo
        </button>
      )}
    </div>
  ) : null;
  return (
    <div
      className={`flex items-start gap-4 p-3 rounded-lg border transition-colors ${
        stagedForRemoval
          ? 'border-danger-300 bg-danger-50/60 opacity-70'
          : stagedReplacement
            ? 'border-primary-300 bg-primary-50/50'
            : captionEdited
              ? 'border-warning-300'
              : 'border-grey-200 hover:border-grey-300'
      }`}
    >
      {thumb ? (
        <button
          type="button"
          onClick={onZoom}
          title="Preview full size"
          className="flex-shrink-0 rounded border border-grey-200 overflow-hidden hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          <img
            src={thumb}
            alt={image.alt || 'image'}
            className={`h-20 w-32 object-cover ${stagedForRemoval ? 'grayscale' : ''}`}
            loading="lazy"
          />
        </button>
      ) : (
        <div className="h-20 w-32 bg-grey-100 rounded flex items-center justify-center text-grey-400 flex-shrink-0">
          <ImageIcon className="h-6 w-6" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap mb-1">
          <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded uppercase tracking-wide ${labelClass}`}>
            {label}
          </span>
          {stagedForRemoval && (
            <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded uppercase tracking-wide bg-danger-100 text-danger-800">
              marked for removal
            </span>
          )}
          {captionEdited && (
            <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded uppercase tracking-wide bg-warning-100 text-warning-800">
              caption edited
            </span>
          )}
          {image.image_role && (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-grey-100 text-grey-700 uppercase">
              {image.image_role}
            </span>
          )}
          {image.image_type && (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-grey-50 text-grey-600 uppercase">
              {image.image_type}
            </span>
          )}
          {provSource === 'entity_reuse' && (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded bg-warning-50 text-warning-800 uppercase">
              reused
            </span>
          )}
          {typeof image.quality_score === 'number' && (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-grey-50 text-grey-600">
              q {image.quality_score.toFixed(2)}
            </span>
          )}
          {typeof image.topic_relevance === 'number' && (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-grey-50 text-grey-600">
              topic {image.topic_relevance.toFixed(2)}
            </span>
          )}
        </div>
        {swapNote}
        <label className="block mt-1">
          <span className="sr-only">Caption</span>
          <textarea
            value={captionValue}
            onChange={(e) => onCaptionChange(e.target.value)}
            disabled={disabled || stagedForRemoval || !image.image_id}
            rows={2}
            maxLength={500}
            placeholder="No caption — type to add one"
            className={`w-full text-sm leading-snug rounded border px-2 py-1.5 resize-y disabled:bg-grey-50 disabled:text-grey-400 ${
              captionEdited
                ? 'border-warning-400 bg-warning-50/50'
                : 'border-grey-200 focus:border-primary-400'
            }`}
          />
        </label>
        <p className="text-[11px] text-grey-400 font-mono mt-1">
          {(image.image_id || '').slice(0, 8)}…
          {image.alt && <span className="ml-2 font-sans text-grey-400">alt: {image.alt.length > 60 ? image.alt.slice(0, 60) + '…' : image.alt}</span>}
        </p>
      </div>
      <div className="flex flex-col gap-2 flex-shrink-0">
        <button
          onClick={onReplace}
          disabled={disabled || replaceDisabled || stagedForRemoval || !image.image_id}
          className="px-3 py-1.5 text-xs font-medium text-grey-700 hover:text-primary-700 border border-grey-300 rounded hover:border-primary-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          title={
            replaceDisabled
              ? 'Apply or discard the staged changes first'
              : 'Replace this image with another from the entity pool'
          }
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Replace
        </button>
        <button
          onClick={onToggleRemove}
          disabled={disabled || !image.image_id}
          className={`px-3 py-1.5 text-xs font-medium border rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 ${
            stagedForRemoval
              ? 'text-grey-700 hover:text-grey-900 border-grey-300 hover:border-grey-400'
              : 'text-danger-700 hover:text-danger-800 border-danger-200 hover:border-danger-400'
          }`}
          title={
            stagedForRemoval
              ? 'Unmark — keep this image'
              : 'Mark this image for removal (applies on Apply)'
          }
        >
          {disabled ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : stagedForRemoval ? (
            <Undo2 className="h-3.5 w-3.5" />
          ) : (
            <X className="h-3.5 w-3.5" />
          )}
          {stagedForRemoval ? 'Undo' : 'Remove'}
        </button>
      </div>
    </div>
  );
}

/**
 * The generated headline card — one per article, rendered by the backend
 * when no announcement image applies (MASTHEAD_HEADLINE_CARDS brief).
 * Accountable mode: it is labelled as generated, with masthead, template
 * version and whether the headline was shortened (D5) shown outright.
 * It is not an image row: no caption, no replace, no removal — the only
 * operator controls are Suppress/Unsuppress and Regenerate (D8).
 */
function HeadlineCardTile({
  state,
  isLiveOnProduction,
  onZoom,
  onRegenerate,
  onSuppress,
  regenerating,
  suppressing,
  blockedByStaging,
  disabled,
}: {
  state: NonNullable<ReturnType<typeof headlineCardState>>;
  isLiveOnProduction: boolean;
  onZoom?: () => void;
  onRegenerate: () => void;
  onSuppress: (suppressed: boolean) => void;
  regenerating: boolean;
  suppressing: boolean;
  blockedByStaging: boolean;
  disabled?: boolean;
}) {
  const busy = regenerating || suppressing;
  const actionsDisabled = disabled || busy || blockedByStaging;
  const stagingTitle = blockedByStaging
    ? 'Apply or discard the staged changes first — this writes immediately'
    : undefined;
  // Stated on the tile, not discovered after the click: both routes queue
  // a republish, and on a live article that reaches readers.
  const liveLine = isLiveOnProduction ? (
    <span className="block text-warning-800">
      This article is live — Regenerate and Suppress republish to readers.
    </span>
  ) : null;

  if (state.kind === 'suppressed') {
    return (
      <div className="mt-3 flex items-center justify-between gap-4 p-3 rounded-lg border border-dashed border-grey-300 bg-grey-50">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
            <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded uppercase tracking-wide bg-grey-200 text-grey-700">
              Generated card suppressed
            </span>
          </div>
          <p className="text-xs text-grey-600">
            The masthead's static default card ships as the LinkedIn image and
            page og:image instead. Unsuppress re-renders the card for this article.
            {liveLine}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onSuppress(false)}
          disabled={actionsDisabled}
          title={stagingTitle ?? 'Re-render the generated card for this article'}
          className="px-3 py-1.5 text-xs font-medium text-grey-700 hover:text-primary-700 border border-grey-300 rounded hover:border-primary-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 flex-shrink-0"
        >
          {suppressing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
          Unsuppress
        </button>
      </div>
    );
  }

  const card = state.entry;
  const prov = card.provenance ?? {};
  const src = card.url || card.social_url || card.thumbnail_url || '';
  return (
    <div className="mt-3 flex items-start gap-4 p-3 rounded-lg border border-grey-200 hover:border-grey-300 transition-colors">
      {src ? (
        <button
          type="button"
          onClick={onZoom}
          title="Preview full size"
          className="flex-shrink-0 rounded border border-grey-200 overflow-hidden hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          {/* 1200×630 — shown whole, not cropped: the layout IS the thing being checked. */}
          <img src={src} alt={card.alt || 'Generated card'} className="h-20 w-[152px] object-contain bg-grey-100" loading="lazy" />
        </button>
      ) : (
        <div className="h-20 w-[152px] bg-grey-100 rounded flex items-center justify-center text-grey-400 flex-shrink-0">
          <LayoutTemplate className="h-6 w-6" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap mb-1">
          <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded uppercase tracking-wide bg-grey-100 text-grey-800">
            Generated card
          </span>
          {prov.masthead && (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-grey-50 text-grey-600 uppercase">
              {prov.masthead}
            </span>
          )}
          {typeof prov.template_version === 'number' && (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-grey-50 text-grey-600">
              template v{prov.template_version}
            </span>
          )}
          {prov.shortened && (
            <span
              className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded bg-warning-50 text-warning-800 uppercase"
              title="The headline was cut at a clause boundary to fit the card (D5)"
            >
              headline shortened
            </span>
          )}
          {prov.synthesis && (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-grey-50 text-grey-600 uppercase">
              synthesis
            </span>
          )}
        </div>
        <p className="text-sm text-grey-800 leading-snug">
          {prov.headline || card.alt || '—'}
        </p>
        <p className="text-[11px] text-grey-500 mt-0.5">
          {[
            prov.ticker && prov.exchange ? `${prov.exchange}:${prov.ticker}` : prov.ticker,
            prov.date,
          ].filter(Boolean).join(' · ')}
        </p>
        <p className="text-xs text-grey-500 mt-1">
          Ships as the LinkedIn image and the page og:image only when no
          announcement image applies. It is not a figure and never renders in
          the body.
          {liveLine}
        </p>
      </div>
      <div className="flex flex-col gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={onRegenerate}
          disabled={actionsDisabled}
          title={stagingTitle ?? 'Re-render the card from the current headline, ticker and date'}
          className="px-3 py-1.5 text-xs font-medium text-grey-700 hover:text-primary-700 border border-grey-300 rounded hover:border-primary-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          {regenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Regenerate
        </button>
        <button
          type="button"
          onClick={() => onSuppress(true)}
          disabled={actionsDisabled}
          title={stagingTitle ?? "Drop the generated card for this article; the masthead's static card ships instead"}
          className="px-3 py-1.5 text-xs font-medium text-danger-700 hover:text-danger-800 border border-danger-200 rounded hover:border-danger-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          {suppressing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
          Suppress
        </button>
      </div>
    </div>
  );
}
